import { KeyboardArrowDown } from '@mui/icons-material'
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  useMediaQuery,
} from '@mui/material'
import type { MouseEvent } from 'react'
import { Fragment, useMemo, useState } from 'react'
import { useCountry, useLocale } from '@/components/Link'
import { getCountryCodes, getCountryDataFromCountryCodes } from './functions'
import { useGetAllVatRegionsQuery } from '@/generated/graphql'
import { useAuthState } from '@/app/providers/auth'
import Image from 'next/image'
import type { Theme } from '@mui/material/styles'
import type { CSSProperties } from 'styled-components'
import { useUserState } from './useUserState'

export function CountryDropdown() {
  const currentLocale = useLocale()
  const currentCountryCode = useCountry()
  const authState = useAuthState()
  const userState = useUserState()
  const isMobileScreen = useMediaQuery('(max-width: 600px)')

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  function handleClick(event: MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget)
  }

  function handleClose(locale?: string) {
    setAnchorEl(null)

    if (!locale || locale === '') {
      return
    }

    if (locale.toLowerCase() === currentLocale?.toLowerCase()) {
      return
    }

    userState.updateUserState({ location: undefined })
  }

  const authenticatedUser = authState.type === 'authenticated' ? authState.user : undefined
  const invoiceAccounts = useMemo(() => {
    return authenticatedUser ? (authenticatedUser.account?.invoiceAccounts ?? []) : []
  }, [authenticatedUser])

  const { data: vatRegionsData } = useGetAllVatRegionsQuery()
  const taxRegions = useMemo(() => vatRegionsData?.vatRegions ?? [], [vatRegionsData])
  const countryCodes = useMemo(
    () => getCountryCodes(invoiceAccounts, taxRegions).sort(),
    [invoiceAccounts, taxRegions],
  )
  const mapped = useMemo(() => getCountryDataFromCountryCodes(countryCodes), [countryCodes])

  if (authState.type !== 'authenticated') {
    return null
  }

  if (invoiceAccounts.length === 0) {
    return null
  }

  const selected = mapped.find((v) => v.label === currentCountryCode)

  return (
    <Fragment>
      <Button
        id="locale-switcher-button"
        type="button"
        aria-haspopup="true"
        aria-controls={open ? 'locale-switcher-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        variant="text"
        startIcon={
          selected && !isMobileScreen ? (
            <Image
              width={18}
              height={14}
              src={selected.flagUri}
              style={{ marginLeft: '4px', marginRight: '2px' } as CSSProperties}
              alt={`${selected.label} flag`}
            />
          ) : null
        }
        endIcon={<KeyboardArrowDown sx={{ ml: -0.5 } as any} />}
        size="medium"
        sx={(t: Theme): CSSProperties => ({
          color: t.palette.grey[700],
          gap: 0,
        })}
      >
        {selected ? (
          selected.label
        ) : (
          <Skeleton variant="rounded" height={20} width={49} sx={{ mr: '0px' } as any} />
        )}
      </Button>

      <Menu
        id="locale-switcher-menu"
        aria-labelledby="locale-switcher-button"
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {mapped.map((country) => (
          <a key={country.label} href={`/${country.locale}/office-catering/vendors`}>
            <MenuItem
              onClick={() => handleClose(country.locale)}
              selected={country.label === currentCountryCode}
            >
              <ListItemIcon>
                <Image
                  width={18}
                  height={13.5}
                  src={country.flagUri}
                  alt={`${country.label} flag`}
                />
              </ListItemIcon>

              <ListItemText>{country.label}</ListItemText>
            </MenuItem>
          </a>
        ))}
      </Menu>
    </Fragment>
  )
}
